// --------------------------------------------------------------------------
// Context and context provider for EOx to exchange data between
// Components like for instance Body and Header.
// --------------------------------------------------------------------------

import React, { createContext, useContext, useState, useEffect,
  useRef } from 'react';
import { GeoJSON as GeoJSONType } from 'geojson';
import axiosAuthInstance from '../context/security';
import { Map } from 'maplibre-gl';
import { CatalogData } from '../types/eox_types';


// Define the shape of the context state and functions
interface EOxContextProps {
  geojsonDataInput: GeoJSONType | null;
  setGeojsonDataInput: (geojsonData: GeoJSONType | null) => void;
  handleGeoJSONChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  mapRef: React.MutableRefObject<Map | null>;
  startDateInput: string;
  setStartDateInput: (startDate: string) => void;
  handleStartDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  endDateInput: string;
  setEndDateInput: (endDate: string) => void;
  handleEndDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  cloudsInput: number;
  setCloudsInput: (clouds: number) => void;
  handleCloudsChange: (resolutionAsString: string, resolutionAsNumber: number) => void;
  fullAoiInput: boolean;
  setFullAoiInput: React.Dispatch<React.SetStateAction<boolean>>;
  handleFullAoiChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  catalogData: CatalogData;
  selectedDatasets: { [key: string]: boolean };
  searchDatasets: string;
  filteredDatasets: string[];
  setSelectedDatasets: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  setSearchDatasets: React.Dispatch<React.SetStateAction<string>>;
  setFilteredDatasets: React.Dispatch<React.SetStateAction<string[]>>;
  handleDatasetsChange: (datasetKey: string, hostKey: string, isChecked: boolean) => void;
  handleDatasetsSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectFilteredDatasets: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dropdownVisible: boolean;
  setDropdownVisible: React.Dispatch<React.SetStateAction<boolean>>;
  searchRef: React.RefObject<HTMLDivElement>;
  errorDataset: string;
  errorInputGeoJSON: string;
  setErrorInputGeoJSON: React.Dispatch<React.SetStateAction<string>>;
}


// Create the context with a default value
export const EOxContext = createContext<EOxContextProps | undefined>(undefined);

export function useEOxContext() {
  const context = useContext(EOxContext);
  if (context === undefined) {
    throw new Error('useEOxContext must be used within a EOxProvider');
  }
  return context;
}

// Create a provider component
export const EOxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [geojsonDataInput, setGeojsonDataInput] = useState<GeoJSONType | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [startDateInput, setStartDateInput] = useState<string>('');
  const [endDateInput, setEndDateInput] = useState<string>('');
  const [cloudsInput, setCloudsInput] = useState<number>(100);
  const [fullAoiInput, setFullAoiInput] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState<{ [key: string]: boolean }>({});
  const [searchDatasets, setSearchDatasets] = useState<string>('');
  const [filteredDatasets, setFilteredDatasets] = useState<string[]>([]);
  const [catalogData, setCatalogData] = useState<CatalogData>({ datasets: {} });
  const [errorDataset, setErrorDataset] = useState('');
  const [errorInputGeoJSON, setErrorInputGeoJSON] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  // Handle data acquisition from data providers and hosts (via backend).
  useEffect(() => {
    const fetchData = async () => {
      setErrorDataset('');
      try {
        const [responseCatalog] = await Promise.all([
          axiosAuthInstance.get('/eox/discover'),
        ]);
        setCatalogData(responseCatalog.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorDataset("Could not fetch dataset information. Please log out and in again.")
      }
    };
    fetchData();
  }, []);

  // Handle datasets selection / input + filter and change
  // Searching datasets is solely based on dataset name (datasetKey)
  // whereas mapping and selection is done using both, the host and dataset
  // information
  const handleDatasetsChange = (datasetKey: string, hostKey: string, isChecked: boolean) => {
    const combinedKey = `${datasetKey}-:-${hostKey}`;
    setSelectedDatasets((prev) => ({
      ...prev,
      [combinedKey]: isChecked,
    }));
  };

  const handleDatasetsSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchDatasets(event.target.value)
    setDropdownVisible(true);
  };

  const clearSearch = () => {
    setSearchDatasets('');
    setDropdownVisible(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        clearSearch();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const filtered = searchDatasets
      ? Object.entries(catalogData.datasets).flatMap(([hostKey, datasetsItem]) => 
          Object.entries(datasetsItem).filter(([datasetKey]) => 
            datasetKey.toLowerCase().includes(searchDatasets.toLowerCase())
          ).map(([datasetKey]) => datasetKey)
        )
      : [];
    setFilteredDatasets(filtered);
  }, [searchDatasets, catalogData]);

  const handleSelectFilteredDatasets = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectedDatasets((prevSelected) => {
      const newSelected = { ...prevSelected };
      filteredDatasets.forEach((datasetKey) => {
        const hostKeys = Object.keys(catalogData.datasets).filter(hostKey => 
          catalogData.datasets[hostKey][datasetKey]);
        hostKeys.forEach((hostKey) => {
          const combinedKey = `${datasetKey}-:-${hostKey}`;
          newSelected[combinedKey] = isChecked;});
      });
      return newSelected;
    });
  };

  // Handle GeoJSON input and change
  const handleGeoJSONChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (value === '') {
      setGeojsonDataInput(null);
      setErrorInputGeoJSON("");
    } else {
      try {
        const geojson = JSON.parse(value);
        setGeojsonDataInput(geojson);
        setErrorInputGeoJSON("");
      } catch (errorGeoJSON) {
        setErrorInputGeoJSON('Invalid GeoJSON Feature.');
      } 
    }
  };

  // Handle start date input and change
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateInput(event.target.value);
  };
  
  // Handle end date input and change
  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDateInput(event.target.value);
  };

  // Handle cloud coverage input and change
  const handleCloudsChange = (cloudsAsString: string, cloudsAsNumber: number) => {
    setCloudsInput(cloudsAsNumber || 100);
  };

  // Handle SRT switch
  const handleFullAoiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullAoiInput(e.target.checked);
  };

  return (
    <EOxContext.Provider value={{
      geojsonDataInput,
      setGeojsonDataInput,
      handleGeoJSONChange,
      mapRef,
      startDateInput,
      setStartDateInput,
      handleStartDateChange,
      endDateInput,
      setEndDateInput,
      handleEndDateChange,
      cloudsInput,
      setCloudsInput,
      handleCloudsChange,
      fullAoiInput,
      setFullAoiInput,
      handleFullAoiChange,
      catalogData,
      selectedDatasets,
      setSelectedDatasets,
      searchDatasets,
      setSearchDatasets,
      filteredDatasets,
      setFilteredDatasets,
      handleDatasetsChange,
      handleDatasetsSearchChange,
      handleSelectFilteredDatasets,
      dropdownVisible,
      setDropdownVisible,
      searchRef,
      errorDataset,
      errorInputGeoJSON,
      setErrorInputGeoJSON,
    }}>
      {children}
    </EOxContext.Provider>
  );
};